import {
  Box,
  Button,
  Checkbox,
  Flex,
  Link,
  Menu,
  MenuButton,
  Alert,
  AlertIcon,
  Tag,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  CellContext,
  useReactTable,
} from '@tanstack/react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  typeguards as commonTypeguards,
  finleyAssert,
} from '@finley-technologies/common';
import { DataLake } from '@finley-technologies/finley-types';

import {
  currentReportingDateColumnName,
  currentValueColumnName,
  finleyReceivableIdColumnName,
  previousReportingDateColumnName,
  previousValueColumnName,
  acknowledgedForColumnName,
  WellKnownFieldToDisplayNames,
  acknowledgedByColumnName,
} from './utils';
import { Counter } from 'packages/common-frontend/components/Counter';
import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import {
  useAcknowledgeForAllDatesMutation,
  useAcknowledgeForSpecificDatesMutation,
  useUnAcknowledgeMutation,
} from '~/core/routes/collateral-data-quality/cdq.gql.generated';
import { ConfirmAcknowledgementModal } from '~/core/routes/collateral-data-quality/ConfirmAcknowledgementModal';
import { colors } from '~/packages/common-frontend/colors';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { remToPx } from '~/packages/common-frontend/theme/utils';

type CollateralDataQualityCheckDetailsProps = {
  cdqId: string;
  queryResults: DataLake.AthenaQueryItemData[];
  receivablesRoutePrefix: string;
  mode: 'acknowledged' | 'unacknowledged';
  refetch: () => void;
};

// renders tabular details of a single CDQ check
export const CollateralDataQualityCheckDetails = ({
  cdqId,
  queryResults,
  receivablesRoutePrefix,
  mode,
  refetch,
}: CollateralDataQualityCheckDetailsProps) => {
  const toast = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [refetching, setRefetching] = useState(false);

  useEffect(() => {
    // if the query results change, reset the row selection
    setRowSelection({});
    setRefetching(false);
  }, [queryResults]);

  useEffect(() => {
    // if the query results change, reset the row selection
    setRowSelection({});
  }, [queryResults]);

  const selectionCount = Object.keys(rowSelection).length;

  const [
    acknowledgeForAllDatesMutation,
    { loading: updating1 },
  ] = useAcknowledgeForAllDatesMutation({
    onError: (err) => {
      toast({
        title: err.message,
        status: 'error',
        isClosable: true,
        position: 'top',
        duration: null, // keep the toast open until user closes it
      });
    },
    onCompleted: () => {
      // ask parent to refetch our data.
      setRefetching(true);
      refetch();
    },
  });

  const [
    acknowledgeForSpecificDatesMutation,
    { loading: updating2 },
  ] = useAcknowledgeForSpecificDatesMutation({
    onError: (err) => {
      toast({
        title: err.message,
        status: 'error',
        isClosable: true,
        position: 'top',
        duration: null, // keep the toast open until user closes it
      });
    },
    onCompleted: () => {
      // ask parent to refetch our data.
      setRefetching(true);
      refetch();
    },
  });

  const [
    unAcknowledgeMutation,
    { loading: updating3 },
  ] = useUnAcknowledgeMutation({
    onError: (err) => {
      toast({
        title: err.message,
        status: 'error',
        isClosable: true,
        position: 'top',
        duration: null, // keep the toast open until user closes it
      });
    },
    onCompleted: () => {
      // ask parent to refetch our data.
      setRefetching(true);
      refetch();
    },
  });

  const updating = updating1 || updating2 || updating3;

  const customColumns = useMemo(() => {
    // some fields are well known, and some are custom fields from CDQ queries.
    // figure out custom fields and to render them generically.
    const firstRow = queryResults.length > 0 ? queryResults[0] : {};
    const wellKnownFieldNames = Object.keys(WellKnownFieldToDisplayNames);
    const customFieldNames = Object.keys(firstRow).filter(
      (field) => !wellKnownFieldNames.includes(field),
    );

    return customFieldNames.map((field) => ({
      id: field,
      accessorKey: field,
      header: field,
      cell: function formatCell(
        ctx: CellContext<DataLake.AthenaQueryItemData, unknown>,
      ) {
        const value = ctx.getValue();
        return (
          <Typography colorScheme="gray">
            {value != null ? value.toString() : '-'}
          </Typography>
        );
      },
    }));
  }, [queryResults]);

  const modeSpecificColumns = useMemo(() => {
    if (mode === 'acknowledged') {
      return [
        {
          id: acknowledgedForColumnName,
          accessorKey: acknowledgedForColumnName,
          header: WellKnownFieldToDisplayNames[acknowledgedForColumnName],
          cell: function formatCell(
            ctx: CellContext<DataLake.AthenaQueryItemData, unknown>,
          ) {
            const value = ctx.getValue();

            if (commonTypeguards.isFullDateStr(value)) {
              return (
                <Typography colorScheme="gray">
                  {commonDate.fullDateStrToHumanDateString(value)}
                </Typography>
              );
            }

            // when a receivable is acknowledged permanently
            return <Tag colorScheme="blue">Permanent</Tag>;
          },
        },
        {
          id: acknowledgedByColumnName,
          accessorKey: acknowledgedByColumnName,
          header: WellKnownFieldToDisplayNames[acknowledgedByColumnName],
          cell: function formatCell(
            ctx: CellContext<DataLake.AthenaQueryItemData, unknown>,
          ) {
            const value = ctx.getValue();
            return value != null ? value.toString() : '-';
          },
        },
      ];
    }
    return [];
  }, [mode]);

  const table = useReactTable({
    data: queryResults,
    columns: [
      ...customColumns,
      ...modeSpecificColumns,
      {
        id: 'selectColumn',
        minSize: remToPx(1),
        size: remToPx(1),
        maxSize: remToPx(1),
        // eslint-disable-next-line react/display-name
        header: ({ table: tableParam }) => (
          <Flex
            as="span"
            marginTop="0.25rem"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <Checkbox
              isChecked={table.getIsAllPageRowsSelected()}
              isIndeterminate={table.getIsSomePageRowsSelected()}
              onChange={() => tableParam.toggleAllPageRowsSelected()}
            />
          </Flex>
        ),
        // eslint-disable-next-line react/display-name
        cell: ({ row }) => (
          <Flex
            as="span"
            marginTop="0.25rem"
            onClick={(event) => {
              // need to wrap chakra checkboxes with a span component to prevent clicks from bubbling up.
              // in this case: we do not want selecting the checkbox to also open a drawer for the row
              event.stopPropagation();
            }}
          >
            <Checkbox
              isChecked={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          </Flex>
        ),
      },
      {
        id: finleyReceivableIdColumnName,
        accessorKey: finleyReceivableIdColumnName,
        header: WellKnownFieldToDisplayNames[finleyReceivableIdColumnName],
        cell: function formatCell({ getValue }) {
          const value = getValue();
          finleyAssert(
            typeof value === 'string',
            `Invalid receivableId value: [${value}]`,
          );
          const shortenedUuid =
            value.length > 12 ? `...${value.slice(-12)}` : value;

          return (
            <Link
              color={colors.blue[500]}
              as={RouterLink}
              to={`${receivablesRoutePrefix}/${value}`}
              target="_blank"
              rel="noopener noreferrer"
              fontFamily="mono"
            >
              {shortenedUuid}
            </Link>
          );
        },
      },
      {
        id: previousReportingDateColumnName,
        accessorKey: previousReportingDateColumnName,
        header: WellKnownFieldToDisplayNames[previousReportingDateColumnName],
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return commonTypeguards.isFullDateStr(value)
            ? commonDate.fullDateStrToHumanDateString(value)
            : '-';
        },
      },
      {
        id: previousValueColumnName,
        accessorKey: previousValueColumnName,
        header: WellKnownFieldToDisplayNames[previousValueColumnName],
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return value != null ? value.toString() : '-';
        },
      },
      {
        id: currentReportingDateColumnName,
        accessorKey: currentReportingDateColumnName,
        header: WellKnownFieldToDisplayNames[currentReportingDateColumnName],
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return commonTypeguards.isFullDateStr(value)
            ? commonDate.fullDateStrToHumanDateString(value)
            : '-';
        },
      },
      {
        id: currentValueColumnName,
        accessorKey: currentValueColumnName,
        header: WellKnownFieldToDisplayNames[currentValueColumnName],
        cell: function formatCell({ getValue }) {
          const value = getValue();
          return value != null ? value.toString() : '-';
        },
      },
    ],
    state: {
      rowSelection,
      sorting,
    },
    initialState: {
      columnPinning: {
        left: ['selectColumn', finleyReceivableIdColumnName],
        right: [],
      },
      sorting,
      pagination: { pageIndex: 0, pageSize: 5 },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onRowSelectionChange: setRowSelection,
  });
  const [showAlert, setShowAlert] = useState(false);
  const configAckDialog = useDisclosure();

  const acknowledgeForAllDates = () => {
    const receivableIds = table.getSelectedRowModel().rows.map((row) => {
      const rowData = row.original;
      const finleyReceivableId = rowData[finleyReceivableIdColumnName];
      finleyAssert(
        typeof finleyReceivableId === 'string',
        `Acknowledgement: Invalid [finleyReceivableId:${finleyReceivableId}] for row ${row.id}`,
      );
      return finleyReceivableId;
    });

    acknowledgeForAllDatesMutation({
      variables: {
        receivableIds,
        cdqId,
      },
    });
  };

  // acknowledge for specific dates
  const acknowledgeForSpecificDates = () => {
    const acknowledgements = table.getSelectedRowModel().rows.map((row) => {
      const rowData = row.original;
      const finleyReceivableId = rowData[finleyReceivableIdColumnName];
      finleyAssert(
        typeof finleyReceivableId === 'string',
        `Acknowledgement: Invalid [finleyReceivableId:${finleyReceivableId}] for row ${row.id}`,
      );
      const reportingDate = rowData[currentReportingDateColumnName];
      finleyAssert(
        commonTypeguards.isFullDateStr(reportingDate),
        `Acknowledgement: Invalid [reportingDate:${reportingDate}] for row ${row.id}`,
      );
      return {
        receivableId: finleyReceivableId,
        reportingDate,
      };
    });

    acknowledgeForSpecificDatesMutation({
      variables: {
        acknowledgements,
        cdqId,
      },
    });
  };

  const unacknowledge = () => {
    const receivableIds = table.getSelectedRowModel().rows.map((row) => {
      const rowData = row.original;
      const finleyReceivableId = rowData[finleyReceivableIdColumnName];
      finleyAssert(
        typeof finleyReceivableId === 'string',
        `Acknowledgement: Invalid [finleyReceivableId:${finleyReceivableId}] for row ${row.id}`,
      );
      return finleyReceivableId;
    });

    unAcknowledgeMutation({
      variables: {
        cdqId,
        receivableIds,
      },
    });
  };

  useEffect(() => {
    if (selectionCount > 0) {
      setShowAlert(false);
    }
  }, [selectionCount]);

  return (
    <Box>
      <Flex my="1rem" gap={4}>
        {['Select All', 'Clear All'].map((label) => (
          <Button
            key={label}
            variant="link"
            colorScheme="blue"
            cursor="pointer"
            onClick={() => {
              const selectAll = label === 'Select All';
              table.toggleAllRowsSelected(selectAll);
            }}
          >
            {label}
          </Button>
        ))}

        <Menu>
          <MenuButton
            as={Button}
            isLoading={refetching}
            rightIcon={<Counter count={selectionCount} />}
            variant="secondary-muted"
            flexShrink={0}
            onClick={(event) => {
              if (selectionCount === 0) {
                setShowAlert(true);
                event.preventDefault();
              } else if (mode === 'acknowledged') {
                // only action available is to unacknowledge
                // just action the button
                unacknowledge();
                event.preventDefault();
              } else if (mode === 'unacknowledged') {
                // if unacknowledged, show the dialog to acknowledge
                configAckDialog.onOpen();
                event.preventDefault();
              }
            }}
          >
            {mode === 'acknowledged' ? 'Unacknowledge' : 'Acknowledge'}
          </MenuButton>
        </Menu>
        {updating && (
          <Tag colorScheme="orange" variant="solid-muted">
            Updating...
          </Tag>
        )}
      </Flex>

      {showAlert && (
        <Alert status="warning" mt="1rem" mb="1rem">
          <AlertIcon />
          Select receivables to{' '}
          {mode === 'acknowledged' ? 'unacknowledge' : 'acknowledge'}.
        </Alert>
      )}

      <ConfirmAcknowledgementModal
        isOpen={configAckDialog.isOpen}
        onClose={configAckDialog.onClose}
        updating={updating}
        onConfirm={(latestOnly) => {
          if (latestOnly) {
            acknowledgeForSpecificDates();
          } else {
            acknowledgeForAllDates();
          }
          configAckDialog.onClose();
        }}
      />

      <TableBETA table={table} />
      <TanStackPaginationControl table={table} />
    </Box>
  );
};
